import { useQuery } from "@tanstack/react-query";
import { EMoneyListTabs } from "pages/v2/Reporting/MoneyList/constants";
import EDaysOnLot from "types/APIDaysOnLot";
import { IDateRanges, IDateRangesV2, IDealership, ISearch, TInventory, TPagination } from "types/APIParameters";
import TResultScoreRecentActivity from "types/APIResultScoreKeys";
import { IAPIDefaultResults } from "types/APIResults";
import fetchData from "utils/fetchData";

export enum EReportsModules {
  NeedsAttentionV2 = "v2/reports/needs-attention",
  RecentActivityReportV2 = "v2/reports/recent-activity-report-dol",
  ResultScoreDetails = "v2/reports/result-score-details",
  MoneyList = "v2/reports/money-list"
}

export type TAdvancedReportOrderKeys = 
  | "carfax_pulls"
  | "-carfax_pulls"
  | "vehicle_year"
  | "-vehicle_year"
  | "stock_number"
  | "-stock_number"
  | "price"
  | "-price"
  | "photos"
  | "-photos"
  | "total_leads"
  | "-total_leads"
  | "total_vdps"
  | "-total_vdps"
  | "leads_vdps"
  | "-leads_vdps"
  | "dol"
  | "-dol"
  | "default_percentage_to_market"
  | "-default_percentage_to_market"
  | "cgr_deal_rating"
  | "-cgr_deal_rating";

interface IParams extends IDealership {}

type TParams = {} & TPagination & IDealership;

export type TMoneyListParams = TParams & IDateRanges & ISearch & {
  report_type?: EMoneyListTabs;
}
  
export type TMoneyListResults = {
  vin: string;
  stock_number: string;
  vehicle: string;
  cgr_deal_rating: string;
  vdp_count: number;
  lead_count: number;
  price: number;
  default_percentage_to_market: number;
  dol: number;
  vdps_dol: number;
  photos_count: number;
  carfax_pull_count: number;
  marketing_vdps_needed: number;
  prioritize: boolean;
  is_certified: boolean;
};

export type TResultScoreDetailsParams = TParams & IDateRangesV2 & ISearch & TInventory & {
  result_score?: number;
};

export type TResultScoreDetailsResults = {
  vin:                              string;
  vehicle:                          string;
  stock_number:                      string;
  leads_score:                 number;
  merchandising_score:       number;
  price_score:                 number;
  result_score:                number;
  vdps_score:                number;
  deal_rating_score:       number;
  is_certified: boolean;
}

interface INeedsAttentionParams extends IParams, IDateRanges, TInventory {}

type IRecentActivityReportParamsV2 = {
  result_score?: number;
  is_missing_photos?: string;
  is_less_than_50_vdps?: string;
  is_less_than_5_asc_leads?: string;
  dol_min?: number;
  dol_max?: number;
} & TParams & IDateRangesV2 & ISearch & TInventory;

type TGreen = number;
type TRed = number;
type TYellow = number;

type TResultsScoreBars = [TGreen, TYellow, TRed];

export interface IAPINeedsAttention {
  [EDaysOnLot.ZeroToFifteen]: TResultsScoreBars;
  [EDaysOnLot.SixteenToThirty]: TResultsScoreBars;
  [EDaysOnLot.ThirtyOneToFortyFive]: TResultsScoreBars;
  [EDaysOnLot.FortySixToSixty]: TResultsScoreBars;
  [EDaysOnLot.SixtyAbove]: TResultsScoreBars;
  green_score: number;
  red_score: number;
  needs_pricing: number;
  yellow_score: number;
}

export type IAPIRecentActivityReportV2 = {
  vin:                          string;
  stock_number:                 string;
  price:                        number;
  photos_count:                 number;
  default_percentage_to_market: number;
  price_to_market:              number;
  last_price_change:            string;
  year:                         number;
  make:                         string;
  model:                        string;
  trim:                         string;
  dol:                          number;
  leads:                        number;
  vdps:                         number;
  total_carfax_pull:            number;
  leads_vdps:                   number;
  result_score:                 number;
  cgr_deal_rating:              string;
  atc_deal_rating:              string;
  ccm_deal_rating:              string;
  cfx_deal_rating:              string;
  vdps_dol:                  number;
  fair_market_range_price:   string | null;
  is_certified: boolean;
}

interface IOptions<T extends string> {
  module: EReportsModules;
  params?: T extends EReportsModules.NeedsAttentionV2
    ? INeedsAttentionParams
    : T extends EReportsModules.RecentActivityReportV2
    ? IRecentActivityReportParamsV2
    : T extends EReportsModules.ResultScoreDetails
    ? TResultScoreDetailsParams
    : T extends EReportsModules.MoneyList
    ? TMoneyListParams
    : IParams;
}

type FetchDataReturnType<T extends IOptions<T>["module"]> =
  T extends EReportsModules.NeedsAttentionV2
    ? IAPINeedsAttention
    : T extends EReportsModules.RecentActivityReportV2
    ? IAPIDefaultResults<IAPIRecentActivityReportV2[]>
    : T extends EReportsModules.ResultScoreDetails
    ? IAPIDefaultResults<TResultScoreDetailsResults[]>
    : T extends EReportsModules.MoneyList
    ? IAPIDefaultResults<TMoneyListResults[]>
    : T;

const useGetReports = <T extends IOptions<T>["module"]>({
  module,
  params,
}: IOptions<T>) => {
  return useQuery({
    refetchOnWindowFocus: false,
    queryFn: getReportsAsync<T>({ module, params }),
    queryKey: ["getReports", module, ...Object.values(params || {})],
    cacheTime: 0,
    staleTime: 0,
    // staleTime: 1000 * 60 * 5, // 5 minutes
    // cacheTime: 1000 * 60 * 5, // 5 minutes
  });
};

export const getReportsAsync = <T extends IOptions<T>["module"]>({
  module,
  params,
}: IOptions<T>) =>
  fetchData<FetchDataReturnType<T>>({
    isAuth: true,
    method: "GET",
    params,
    url: `${process.env.REACT_APP_API_URL || ""}api/${
      module ? `${module}` : ""
    }`,
  });

export default useGetReports;
